// Plugin for global events and other custom methods

"use strict";

import { App } from "vue";
import { AppEventsMap } from "./control/app-events";
import { autoFocusElement } from "./composables/useAutoFocus";
import { closeModalContainer } from "./composables/useModal";
import { getCaptchaToken } from "./composables/useCaptcha";
import { askUserConfirmation, showMessageModal, showProfileModal, showSnackBar } from "./composables/useAppUi";
import { goBackFromLoginNavigation, requireLoginNavigation } from "./composables/useAuthNavigation";
import { resetPageTitle, setPageSubTitle } from "./composables/usePageTitle";
import {
    listenOnAppEvent,
    listenOnDocumentEvent,
    removeAppEventListener,
    removeDocumentEventListener,
} from "./composables/useAppEventListener";
import { HOME_ROUTE, UNAUTHORIZED_ONLY_ROUTES } from "./navigation-constants";

export { HOME_ROUTE, UNAUTHORIZED_ONLY_ROUTES };

type CallbackFunctionVariadic = (...args: any[]) => void;

declare module "@vue/runtime-core" {
    interface ComponentCustomProperties {
        /**
         * Override refs
         */
        $refs: any;

        /**
         * Mapping of app event handlers
         */
        $appEventHandlers: Map<string, CallbackFunctionVariadic>;

        /**
         * Mapping of document event handlers
         */
        $documentEventHandlers: Map<string, CallbackFunctionVariadic>;

        /**
         * Listens to a global custom App event, and removes the listener after the component has been removed
         * @param eventName Event name
         * @param handler Event handler function
         */
        $listenOnAppEvent: <K extends keyof AppEventsMap>(eventName: K, handler: AppEventsMap[K]) => void;

        /**
         * Listens to a document
         * @param eventName Event name
         * @param handler Event handler function
         */
        $listenOnDocumentEvent: <K extends keyof DocumentEventMap>(
            eventName: K,
            listener: (this: Document, ev: DocumentEventMap[K]) => any,
        ) => void;

        /**
         * Closes the current modal, if the component has a 'modalContainer' reference
         * @param forced True to ignore the close callback
         */
        $closeModal: (forced?: boolean) => void;

        /**
         * Automatically focus an element
         * @param className The class of the element. By default, auto-focus
         */
        $autoFocus: (className?: string) => void;

        /**
         * Call on authorized to request the user to login
         */
        $requireLogin: () => void;

        /**
         * Redirects to the page where the login was required
         */
        $goBackFromLogin: () => void;

        /**
         * The title of the page
         */
        $subTitle?: string;

        /**
         * Set sub-title for pages
         * @param title The title to set
         */
        $setSubTitle: (title: string) => void;

        /**
         * Displays a message in the Snack Bar
         * @param msg Message text
         * @param position Optional horizontal position
         */
        $showSnackBar: {
            (msg: string, position?: "left" | "center" | "right"): void;
            (options: import("./control/app-events").SnackBarOptions): void;
        };

        /**
         * Show a message modal
         * @param title The title
         * @param msg The message
         */
        $showMessageModal: (title: string, msg: string) => void;

        /**
         * Shows a profile modal
         * @param uid The user ID
         */
        $showProfileModal: (uid: string) => void;

        /**
         * Asks for user confirmation
         * @param options The options
         */
        $askUserConfirmation: (options: { title: string; message: string; danger?: boolean; callback: () => void }) => void;

        /**
         * Gets a captcha
         * @param subject The subject
         * @returns The captcha (as a promise that never rejects)
         */
        $getCaptcha: (subject: string) => Promise<string>;
    }
}

export const appEventsPlugin = {
    install: (app: App) => {
        app.mixin({
            methods: {
                $listenOnAppEvent: function <K extends keyof AppEventsMap>(eventName: K, handler: AppEventsMap[K]) {
                    if (!this.$appEventHandlers) {
                        this.$appEventHandlers = new Map();
                    }
                    if (this.$appEventHandlers.has(eventName)) {
                        throw new Error("Already listening for app event '" + eventName + "' on this component");
                    }
                    this.$appEventHandlers.set(eventName, handler);
                    listenOnAppEvent(eventName, handler);
                },
                $listenOnDocumentEvent: function <K extends keyof DocumentEventMap>(
                    eventName: K,
                    listener: (this: Document, ev: DocumentEventMap[K]) => any,
                ) {
                    if (!this.$documentEventHandlers) {
                        this.$documentEventHandlers = new Map();
                    }
                    if (this.$documentEventHandlers.has(eventName)) {
                        throw new Error("Already listening for document event '" + eventName + "' on this component");
                    }
                    this.$documentEventHandlers.set(eventName, listener);
                    listenOnDocumentEvent(eventName, listener);
                },
                $closeModal: function (forced?: boolean) {
                    closeModalContainer(this.$refs.modalContainer, forced);
                },
                $autoFocus: function (className?: string) {
                    autoFocusElement(this.$el, className);
                },
                $requireLogin: function () {
                    requireLoginNavigation(
                        this.$router,
                        this.$route,
                        (forced) => this.$closeModal(forced),
                        () => this.$requiredLogin,
                        (value) => {
                            this.$requiredLogin = value;
                        },
                    );
                },
                $goBackFromLogin: function () {
                    goBackFromLoginNavigation(this.$router);
                },
                $setSubTitle: function (title: string) {
                    this.$subTitle = title;
                    setPageSubTitle(title);
                },
                $showSnackBar: showSnackBar,
                $showMessageModal: showMessageModal,
                $askUserConfirmation: askUserConfirmation,
                $showProfileModal: showProfileModal,
                $getCaptcha: async function (subject: string): Promise<string> {
                    return getCaptchaToken(subject, {
                        recaptchaLoaded: () => this.$recaptchaLoaded(),
                        executeRecaptcha: (action) => this.$recaptcha(action),
                    });
                },
            },
            beforeUnmount() {
                if (this.$subTitle) {
                    resetPageTitle();
                }
                this.$appEventHandlers &&
                    this.$appEventHandlers.forEach((handler, eventName) => {
                        removeAppEventListener(eventName, handler);
                    });
                this.$documentEventHandlers &&
                    this.$documentEventHandlers.forEach((listener, eventName) => {
                        removeDocumentEventListener(eventName, listener);
                    });
            },
        });
    },
};
